import * as React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { BlankSlate, Box, SearchField } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import useIntercom from 'modules/onboarding/hooks/useIntercom'
import ReferalProgramBanner from 'modules/referral_program/components/ReferralProgramBanner'
import NoResults from 'modules/settings/components/NoResults'
import SettingsSection from 'modules/settings/components/Section'
import { useSearchSettings } from 'modules/settings/hooks/useSearchSettings'
import { useSettings } from 'modules/settings/hooks/useSettings'

const SettingsSearchContext = React.createContext<string | null>(null)

const Content = () => {
  const [searchEntry, setSearchEntry] = React.useState('')
  const startSearch = React.useRef(false)
  const { settings } = useSettings()

  const [searchParams] = useSearchParams()
  const productTourId = searchParams.get('product_tour_id')

  const session = useCurrentSession()
  const navigate = useNavigate()

  const {
    email,
    access: { id: userId, firstName, lastName },
    company: {
      id: companyId,
      name: companyName,
      size: companySize,
      industry: companyIndustry,
      createdAt: companyCreatedAt,
      legalEntities,
    },
    employee,
    isIntercomAllowed,
  } = session
  const userIsAdmin = false

  const shouldLoadIntercom = Boolean(productTourId) && isIntercomAllowed

  const { handleStartTour } = useIntercom({
    isIntercomAllowed: shouldLoadIntercom,
    userIsAdmin,
    userName: `${firstName} ${lastName}`,
    userId,
    userEmail: email,
    userPhone: employee?.phoneNumber ?? '',
    companyId,
    companyCreatedAt,
    companyName,
    companySize,
    companyIndustry,
    companyCountry: legalEntities[0]?.country ?? '',
  })

  const results = useSearchSettings(searchEntry, settings)

  const updateSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchEntry(e.target.value)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && results.length === 1 && results[0]?.children?.length === 1) {
      navigate(results[0]?.children[0]?.url ?? '')
    }
  }

  const settingsSections = results.map((resultSettings, index) => (
    <SettingsSection key={index} settings={resultSettings} />
  ))

  useEffect(() => {
    if (searchEntry !== '' && results.length > 0) {
      track(trackingEvents.settings.searchSubmit, { search_keyword: searchEntry })
    }
    if (searchEntry !== '' && results.length === 0) {
      track(trackingEvents.settings.searchEmpty, { search_keyword: searchEntry })
    }
  }, [searchEntry, results.length])

  useEffect(() => {
    track(trackingEvents.settings.viewHomepage)
  }, [])

  useEffect(() => {
    handleStartTour(Number(productTourId))
  }, [productTourId, shouldLoadIntercom])

  return (
    <ResponsivePage.Body variant="full" showHeader={false}>
      {settings.length === 0 ? (
        <Box alignItems="center" width="full" paddingX="s48" paddingTop="s48" gap="s32">
          <BlankSlate
            illustration="settings"
            title={i18n.t('settings.empty.title')}
            description={i18n.t('settings.empty.description')}
          />
        </Box>
      ) : (
        <>
          <ReferalProgramBanner />
          <Box maxWidth="s400" paddingX="s48" paddingY="s16">
            <div onKeyDown={(e) => onKeyDown(e)}>
              <SearchField
                variant="big"
                onSearch={(e: React.ChangeEvent<HTMLInputElement>) => updateSearch(e)}
                placeholder={i18n.t('company_settings.search.placeholder')}
                search={searchEntry}
                onClick={() => {
                  if (searchEntry === '' && !startSearch.current) {
                    track(trackingEvents.settings.searchStart)
                    startSearch.current = true
                  }
                }}
                onBlur={() => {
                  if (searchEntry === '') {
                    startSearch.current = false
                  }
                }}
                autofocus
              />
            </div>
          </Box>
          <SettingsSearchContext.Provider value={searchEntry}>
            {results.length > 0 ? settingsSections : <NoResults />}
          </SettingsSearchContext.Provider>
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default Content
